import type { Ref } from 'vue'

interface StripeResponse {
  client_secret: string
}

const unwrap = <T>(val: T | Ref<T>) => (isRef(val) ? val.value : val)
const getElement = <T>(ref: T | T[]) => (Array.isArray(ref) ? ref[0] : ref)

const baseOptions = {
  style: {
    style: {
      base: {
        color: '#000',
        iconColor: '#000',
        textTransform: 'uppercase',
        fontWeight: '500',
        fontFamily: 'League Spartan, sans-serif',
        '::placeholder': {
          color: '#17314B',
        },
      },
    },
  },
  element: undefined,
}

interface RequiredFields {
  amount: number
  name: string
  email: string
  currency: string
  reference?: string
}

const card = ref<HTMLElement | null>(null)

export const setCardElement = (el: HTMLElement | null) => (card.value = el)

export default function useStripe<T extends RequiredFields>(
  stripePublicKey: string,
  submission: ComputedRef<T> | T,
  errorMessage: Ref<string>,
  completionHandler: () => void,
  enabled: Ref<boolean>,
  options = baseOptions
) {
  const cardElement = ref<stripe.elements.Element | null>(null)

  const clientSecret = ref<string | null>(null)
  const stripe = ref<stripe.Stripe | null>(null)
  const state = ref<
    | 'ready'
    | 'error'
    | 'submitting'
    | 'getting intent'
    | 'submitted'
    | 'generating invoice'
  >('ready')
  const cardComplete = ref(false)

  function displayError(pos: string, err: unknown = '') {
    let error: string
    if (err instanceof Error) error = err.message
    error = String(err)

    errorMessage.value = pos + ', ' + error
    state.value = 'error'
  }

  let initialised = false

  async function initialiseStripeElement() {
    try {
      if (initialised) return
      initialised = true

      stripe.value = Stripe(stripePublicKey || '')

      const elements = stripe.value.elements(options.element)

      cardElement.value = elements.create('card', options.style)
      await cardElement.value.mount(getElement(card.value))
      // console.log('init', cardElement.value)

      cardElement.value.addEventListener('change', (newstate) => {
        if (!newstate) return
        if (newstate.value) {
          cardComplete.value = newstate.complete
          state.value = 'ready'
        }
        if (newstate.error)
          displayError('init newstate', newstate.error.message)
      })
    } catch (err) {
      displayError('init', err)
    }
  }

  watch(
    () => [enabled, card.value, cardElement.value],
    () => {
      if (enabled && card.value && !cardElement.value) initialiseStripeElement()
    },
    { immediate: true }
  )

  const isFormComplete = computed<boolean>(() => {
    const details = unwrap(submission)
    return (
      !!details.email &&
      !!details.amount &&
      !!details.name &&
      cardComplete.value
    )
  })

  async function getIntent() {
    if (!unwrap(enabled)) return
    state.value = 'getting intent'
    // console.log('getting intent', submission)
    try {
      const { client_secret } = await $fetch<
        StripeResponse | Record<string, unknown>
      >('/api/checkout', {
        method: 'POST',
        body: unwrap(submission),
      })
      if (!client_secret) return console.error("!('client_secret' in data)")
      clientSecret.value = client_secret as string
      state.value = 'ready'
    } catch (e) {
      console.error('getting intent', e)
      displayError('getting intent', e)
    }
  }

  const debouncedGetIntent = debounce(getIntent, 500)

  watch(
    () => [isFormComplete.value, submission],
    ([types]) =>
      isFormComplete.value &&
      Object.entries(types).some(([_type, isComplete]) => isComplete) &&
      debouncedGetIntent()
  )

  async function submit() {
    try {
      if (!unwrap(enabled)) return completionHandler()
      if (!clientSecret.value) {
        // console.log('submit getting intent')
        await getIntent()
      }

      if (!stripe.value) {
        displayError('submit Stripe', 'Error loading payment provider.')
        console.error('submit Stripe', stripe.value)
        throw new Error('Error loading Stripe.')
      }
      if (!cardElement.value) {
        displayError('submit Stripe element', 'Error loading payment provider.')
        console.error('submit Stripe element', cardElement.value)
        throw new Error('Error loading Stripe element.')
      }
      if (!clientSecret.value) {
        displayError('submit Stripe secret', 'Error loading payment provider.')
        console.error('submit Stripe secret', clientSecret.value)
        throw new Error('Error loading Stripe code.')
      }

      state.value = 'submitting'
      window.onbeforeunload = () => 'Are you sure you want to navigate away?'

      const { error } = await stripe.value.confirmCardPayment(
        clientSecret.value,
        {
          payment_method: {
            card: cardElement.value,
            billing_details: {
              name: unwrap(submission).name,
              email: unwrap(submission).email,
            },
          },
          return_url: 'https://drgrahamshomes.co.uk/',
        }
      )
      if (error) displayError('submit general', error.message)
      else completionHandler()
    } catch (error) {
      displayError('submit', error)
    }
  }

  return {
    submission,
    state,
    cardComplete,
    submit,
    isFormComplete,
  }
}
